/**
 * Relay verification — Settings → Network "Test relays" (NET-REL diagnostics).
 * Connects, sends a query that matches nothing and waits for ANY reply (EOSE,
 * CLOSED, NOTICE, AUTH). The old probe never sent a frame, so relays that do
 * not greet on connect were misreported as "timeout/down".
 */

export interface RelayProbeResult {
  url: string;
  ok: boolean;
  /** Relay sent an AUTH challenge (NIP-42 or relay-dev). */
  requiresAuth: boolean;
  latencyMs: number;
  error?: string;
}

export type RelayProbe =
  | { kind: "start"; url: string }
  | { kind: "result"; url: string; result: RelayProbeResult };

const PROBE_TIMEOUT_MS = 5_000;

export function probeRelay(url: string, timeoutMs = PROBE_TIMEOUT_MS): Promise<RelayProbeResult> {
  return new Promise((resolve) => {
    const started = Date.now();
    let settled = false;
    let sawChallenge = false;
    let ws: WebSocket | null = null;
    const finish = (ok: boolean, error?: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      resolve({ url, ok, requiresAuth: sawChallenge, latencyMs: Date.now() - started, ...(error ? { error } : {}) });
    };
    const timer = setTimeout(() => finish(false, "timeout"), timeoutMs);
    try {
      ws = new WebSocket(url);
    } catch (e) {
      finish(false, e instanceof Error ? e.message : "invalid url");
      return;
    }
    ws.onopen = () => {
      try {
        ws?.send(JSON.stringify(["REQ", "veto-probe", { ids: ["0".repeat(64)], limit: 1 }]));
      } catch {
        finish(false, "send failed");
      }
    };
    ws.onmessage = (m) => {
      try {
        const data = JSON.parse(String(m.data)) as unknown[];
        if (data[0] === "AUTH") sawChallenge = true;
        if (data[0] === "EOSE" || data[0] === "CLOSED" || data[0] === "NOTICE" || data[0] === "AUTH") {
          finish(true);
        }
      } catch {
        /* ignore non-JSON */
      }
    };
    ws.onerror = () => finish(false, "connection error");
    ws.onclose = () => finish(false, "closed");
  });
}

/** Probe many relays with bounded concurrency; never rejects. */
export async function probeRelays(
  urls: string[],
  opts: { timeoutMs?: number; concurrency?: number; onProgress?: (p: RelayProbe) => void } = {},
): Promise<RelayProbeResult[]> {
  const queue = [...new Set(urls)];
  const results: RelayProbeResult[] = [];
  const workers = Array.from({ length: Math.max(1, opts.concurrency ?? 4) }, async () => {
    while (queue.length) {
      const url = queue.shift()!;
      opts.onProgress?.({ kind: "start", url });
      const result = await probeRelay(url, opts.timeoutMs);
      results.push(result);
      opts.onProgress?.({ kind: "result", url, result });
    }
  });
  await Promise.all(workers);
  return results;
}
